import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useApi } from "../../containers/ApiContainer";
import { useIdentity } from "../../containers/IdentityContainer";
import { useTokenMaker } from "../../hooks";
import { usePendingInvites } from "../../hooks/useInvitations";
// import logo from "../../../airplx-logo-transparent-square-no-text.png";
const logo = "/airplx-logo-transparent-square-no-text.png";

export const NoActiveFBO: React.FC<{}> = () => {
  const { t } = useTranslation();
  const { airplxIdentity } = useIdentity();
  const { pendingInvites, loading } = usePendingInvites();
  const navigate = useNavigate();
  const theme = useTheme();
  const { url: tokenMakerURL } = useTokenMaker();
  const { activeFBO, fbos, ready, setActiveFBOId } = useActiveFBOs();
  const { postgrest } = useApi();
  const { inviteId } = useParams<{ inviteId?: string }>();

  // Determine user's FBO context
  const hasFBOs = fbos && fbos.length > 0;

  // State for tracking which invites are being claimed
  const [claimingInvites, setClaimingInvites] = React.useState<Set<string>>(
    new Set()
  );

  // State for tracking errors on specific invites
  const [errorInvites, setErrorInvites] = React.useState<Map<string, string>>(
    new Map()
  );

  // State for user feedback snackbar
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  // State for direct-link invite (from URL parameter)
  const [directLinkInvite, setDirectLinkInvite] = React.useState<{
    id: string;
    fbo_id: string;
    fbo_name: string;
    is_valid: boolean;
  } | null>(null);
  const [directLinkLoading, setDirectLinkLoading] = React.useState(false);

  // Validation function that mirrors ClaimInvite.tsx
  const validateInvite = async (inviteId: string) => {
    const { data } = await postgrest
      .rpc("is_invite_valid", {
        invite_id: inviteId,
        email: airplxIdentity?.email,
      })
      .single();
    return data;
  };

  // Comprehensive claim function with validation and error handling
  const handleClaimInvite = async (inviteId: string) => {
    // Clear any previous error for this invite
    setErrorInvites((prev) => {
      const newMap = new Map(prev);
      newMap.delete(inviteId);
      return newMap;
    });

    // Guard clauses - check required dependencies
    if (!airplxIdentity?.userid) {
      setErrorInvites((prev) =>
        new Map(prev).set(inviteId, t("screens.noActiveFBO.errors.authRequired"))
      );
      setSnackbar({
        open: true,
        message: t("screens.noActiveFBO.errors.loginAgain"),
        severity: "error",
      });
      return;
    }

    if (!tokenMakerURL) {
      setErrorInvites((prev) =>
        new Map(prev).set(inviteId, t("screens.noActiveFBO.errors.serviceUnavailable"))
      );
      setSnackbar({
        open: true,
        message: t("screens.noActiveFBO.errors.serviceTemporarilyUnavailable"),
        severity: "error",
      });
      return;
    }

    if (!airplxIdentity?.email) {
      setErrorInvites((prev) => new Map(prev).set(inviteId, t("screens.noActiveFBO.errors.emailRequired")));
      setSnackbar({
        open: true,
        message: t("screens.noActiveFBO.errors.emailRequiredValidation"),
        severity: "error",
      });
      return;
    }

    // Add to claiming set for loading state
    setClaimingInvites((prev) => new Set([...prev, inviteId]));

    try {
      // Step 1: Validate invite first (mirrors ClaimInvite.tsx)
      const validation = await validateInvite(inviteId);

      if (!validation?.is_valid) {
        throw new Error(
          t("screens.noActiveFBO.errors.inviteInvalid")
        );
      }

      // Use FBO ID from validation (not from pending invites data)
      const validatedFboId = validation.fbo_id;

      if (!validatedFboId) {
        throw new Error(t("screens.noActiveFBO.errors.invalidFBOInfo"));
      }

      // Step 2: Claim the invite via tokenMaker API
      const response = await fetch(
        `${tokenMakerURL}/claim/invite/${inviteId}/${airplxIdentity.userid}`
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to claim invite: ${errorText}`);
      }

      const result = await response.json();
      if (!result.did_claim) {
        throw new Error(t("screens.noActiveFBO.errors.inviteCouldNotBeClaimed"));
      }

      // Step 3: Set active FBO using validated FBO ID
      // No need to refresh token - JWT doesn't include FBO memberships anymore.
      // The page reload will fetch updated FBO memberships from the database.
      setActiveFBOId(validatedFboId);

      // Step 5: Show success feedback
      setSnackbar({
        open: true,
        message: `Successfully joined ${validation.name}!`,
        severity: "success",
      });

      // Step 6: Navigate home (same as ClaimInvite.tsx)
      navigate("/");
      window.location.reload();
    } catch (error) {
      console.error("Error claiming invite:", error);

      const errorMessage =
        error instanceof Error ? error.message : t("screens.noActiveFBO.errors.acceptFailed");

      setErrorInvites((prev) => new Map(prev).set(inviteId, errorMessage));
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });

      // Remove from claiming set on error
      setClaimingInvites((prev) => {
        const newSet = new Set(prev);
        newSet.delete(inviteId);
        return newSet;
      });
    }
  };

  // Fetch direct-link invite when inviteId is present in URL
  React.useEffect(() => {
    const fetchDirectLinkInvite = async () => {
      if (!inviteId || !postgrest) return;

      setDirectLinkLoading(true);
      try {
        const validation = await validateInvite(inviteId);

        if (validation?.is_valid) {
          setDirectLinkInvite({
            id: inviteId,
            fbo_id: validation.fbo_id,
            fbo_name: validation.name,
            is_valid: true,
          });
        } else {
          // Invalid invite - show error
          setSnackbar({
            open: true,
            message:
              "This invitation link is invalid or has already been claimed",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error fetching direct-link invite:", error);
        setSnackbar({
          open: true,
          message: "Failed to load invitation. Please try again.",
          severity: "error",
        });
      } finally {
        setDirectLinkLoading(false);
      }
    };

    fetchDirectLinkInvite();
  }, [inviteId, postgrest]);

  // Helper functions for invite status and styling
  const getInviteCardSx = (invite: any) => ({
    transition: "all 0.2s ease-in-out",
    "&:hover": invite.is_expired
      ? {}
      : {
          elevation: 4,
          transform: "translateY(-2px)",
          boxShadow: theme.shadows[8],
        },
    borderRadius: 2,
    overflow: "hidden",
    opacity: invite.is_expired ? 0.7 : 1,
    border: invite.is_expired
      ? `1px solid ${theme.palette.error.light}`
      : "none",
  });

  const getStatusChip = (invite: any) => (
    <Chip
      label={invite.is_expired ? "Expired" : "Pending"}
      size="small"
      color={invite.is_expired ? "error" : "warning"}
      variant="outlined"
      sx={
        invite.is_expired
          ? {
              backgroundColor: theme.palette.error.light,
              color: theme.palette.error.contrastText,
            }
          : {
              backgroundColor: theme.palette.warning.light,
              color: theme.palette.warning.contrastText,
            }
      }
    />
  );

  const getActionButton = (invite: any) => {
    if (invite.is_expired) {
      return (
        <Button
          variant="outlined"
          size="large"
          disabled
          sx={{
            minWidth: 120,
            fontWeight: "medium",
            color: theme.palette.error.main,
            borderColor: theme.palette.error.main,
          }}
        >
          Expired
        </Button>
      );
    }

    // For valid invites, use existing button logic
    return (
      <Button
        variant="contained"
        size="large"
        onClick={() => handleClaimInvite(invite.id)}
        disabled={claimingInvites.has(invite.id)}
        sx={{
          minWidth: 120,
          fontWeight: "medium",
          ...getButtonColor(invite.id),
        }}
      >
        {getButtonContent(invite.id)}
      </Button>
    );
  };

  // Helper functions for button state management (for valid invites only)
  const getButtonState = (inviteId: string) => {
    if (claimingInvites.has(inviteId)) return "claiming";
    if (errorInvites.has(inviteId)) return "error";
    return "default";
  };

  const getButtonContent = (inviteId: string) => {
    const state = getButtonState(inviteId);
    switch (state) {
      case "claiming":
        return (
          <>
            <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
            {t("screens.noActiveFBO.inviteStatus.accepting")}
          </>
        );
      case "error":
        return t("screens.noActiveFBO.inviteStatus.tryAgain");
      default:
        return t("screens.noActiveFBO.inviteStatus.accept");
    }
  };

  const getButtonColor = (inviteId: string) => {
    const state = getButtonState(inviteId);
    switch (state) {
      case "error":
        return {
          backgroundColor: theme.palette.error.main,
          "&:hover": {
            backgroundColor: theme.palette.error.dark,
          },
        };
      default:
        return {
          backgroundColor: theme.palette.primary.main,
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
          },
        };
    }
  };

  // Navigation helpers for existing FBO users
  const handleReturnToDashboard = () => {
    const targetFBO = activeFBO || fbos?.[0];
    if (targetFBO && targetFBO.id !== activeFBO?.id) {
      setActiveFBOId(targetFBO.id);
    }
    navigate("/hangars");
  };

  const handleSwitchFBO = () => {
    navigate("/settings");
  };

  // Context-aware messaging functions
  const getHeaderMessage = () => {
    if (!ready) return null; // Still loading FBO data

    if (!hasFBOs) {
      // No FBOs - show invite-specific messages
      if (loading) return null;

      const validInvites = pendingInvites.filter(
        (invite) => !invite.is_expired
      );
      const expiredInvites = pendingInvites.filter(
        (invite) => invite.is_expired
      );

      if (validInvites.length > 0 && expiredInvites.length > 0) {
        return (
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.warning.main,
              fontWeight: "medium",
              textAlign: "center",
              mb: 3,
            }}
          >
            📋 You have {validInvites.length} active and {expiredInvites.length}{" "}
            expired invitation{expiredInvites.length > 1 ? "s" : ""}
          </Typography>
        );
      } else if (validInvites.length > 0) {
        return (
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.success.main,
              fontWeight: "medium",
              textAlign: "center",
              mb: 3,
            }}
          >
            🎉 You have pending invitations!
          </Typography>
        );
      } else if (expiredInvites.length > 0) {
        return (
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.error.main,
              fontWeight: "medium",
              textAlign: "center",
              mb: 3,
            }}
          >
            ⏰ Your invitation{expiredInvites.length > 1 ? "s have" : " has"}{" "}
            expired
          </Typography>
        );
      }
    } else {
      // Has FBOs - show additional invite context
      if (loading) return null;

      if (pendingInvites.length > 0) {
        return (
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.info.main,
              fontWeight: "medium",
              textAlign: "center",
              mb: 3,
            }}
          >
            🏢 Additional FBO Invitations
          </Typography>
        );
      } else {
        return (
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: "medium",
              textAlign: "center",
              mb: 3,
            }}
          >
            👋 Welcome back!
          </Typography>
        );
      }
    }

    return null;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "60vh",
          justifyContent: "center",
        }}
      >
        {/* Header with Logo */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
          <Box
            component="img"
            src={logo}
            alt={t("screens.noActiveFBO.logoAlt")}
            sx={{
              width: 56,
              height: 56,
              border: "2px solid",
              borderColor: theme.palette.primary.main,
              borderRadius: "50%",
            }}
          />
          <Typography variant="h3" color="primary" fontWeight="bold">
            {t("screens.noActiveFBO.appName")}
          </Typography>
        </Stack>
        {loading || !ready || directLinkLoading ? (
          // Loading State (includes both invite loading and FBO loading)
          <Box sx={{ width: "100%", maxWidth: 600 }}>
            <Skeleton
              variant="text"
              width={300}
              height={40}
              sx={{ mb: 3, mx: "auto" }}
            />
            <Stack spacing={3}>
              {[1, 2].map((index) => (
                <Card key={index} elevation={2} sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" spacing={3} alignItems="center">
                      <Stack flex={1} spacing={1}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Skeleton variant="text" width={120} height={32} />
                          <Skeleton
                            variant="rectangular"
                            width={60}
                            height={24}
                            sx={{ borderRadius: 1 }}
                          />
                        </Stack>
                        <Skeleton variant="text" width={100} height={20} />
                      </Stack>
                      <Skeleton
                        variant="rectangular"
                        width={120}
                        height={40}
                        sx={{ borderRadius: 1 }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        ) : directLinkInvite || pendingInvites.length > 0 ? (
          // Has pending invites (for users with or without existing FBOs)
          <Box sx={{ width: "100%", maxWidth: 600 }}>
            {/* Context-aware header message */}
            {getHeaderMessage()}

            {/* Show current FBO context if user has FBOs */}
            {hasFBOs && (
              <Card variant="outlined" sx={{ mb: 3, textAlign: "center" }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Currently active: <strong>{activeFBO?.name}</strong>
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    sx={{ mt: 1 }}
                  >
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleReturnToDashboard}
                      sx={{ fontSize: "0.75rem" }}
                    >
                      Return to Dashboard
                    </Button>
                    <Button
                      size="small"
                      onClick={handleSwitchFBO}
                      sx={{ fontSize: "0.75rem" }}
                    >
                      Switch FBO
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Direct-link invite from URL (shown prominently if present) */}
            {directLinkInvite && (
              <>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: theme.palette.info.main,
                    fontWeight: "medium",
                    mb: 2,
                  }}
                >
                  📧 Invitation from Email Link
                </Typography>
                <Card
                  elevation={3}
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    border: `2px solid ${theme.palette.primary.main}`,
                    mb: 3,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" spacing={3} alignItems="center">
                      <Stack flex={1} spacing={1}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography variant="h6" fontWeight="medium">
                            {directLinkInvite.fbo_name}
                          </Typography>
                          <Chip
                            label={t("screens.noActiveFBO.inviteStatus.pending")}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{
                              backgroundColor: theme.palette.primary.light,
                              color: theme.palette.primary.contrastText,
                            }}
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          Click Accept to join this FBO
                        </Typography>
                      </Stack>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => handleClaimInvite(directLinkInvite.id)}
                        disabled={claimingInvites.has(directLinkInvite.id)}
                        sx={{
                          minWidth: 120,
                          fontWeight: "medium",
                          ...getButtonColor(directLinkInvite.id),
                        }}
                      >
                        {getButtonContent(directLinkInvite.id)}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </>
            )}

            <Stack spacing={3}>
              {pendingInvites
                .filter((invite) => invite.id !== directLinkInvite?.id)
                .map((invite) => (
                  <Card
                    key={invite.id}
                    elevation={2}
                    sx={getInviteCardSx(invite)}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Stack direction="row" spacing={3} alignItems="center">
                        {/* Invitation Details */}
                        <Stack flex={1} spacing={1}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography
                              variant="h6"
                              fontWeight="medium"
                              sx={{
                                color: invite.is_expired
                                  ? theme.palette.text.secondary
                                  : "inherit",
                              }}
                            >
                              {invite.fbo?.name || "Unknown FBO"}
                            </Typography>
                            {getStatusChip(invite)}
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            Invited{" "}
                            {new Date(invite.created_at).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              }
                            )}
                          </Typography>
                          {invite.is_expired && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.error.main,
                                fontStyle: "italic",
                                mt: 0.5,
                              }}
                            >
                              This invitation has expired. Please contact your
                              FBO administrator for a new invitation.
                            </Typography>
                          )}
                        </Stack>

                        {/* Action Button */}
                        {getActionButton(invite)}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
            </Stack>
          </Box>
        ) : (
          // No pending invites - different messages based on FBO context
          <Box sx={{ width: "100%", maxWidth: 500 }}>
            {/* Context-aware header */}
            {getHeaderMessage()}

            {hasFBOs ? (
              // User has FBOs but no pending invites
              <Card
                sx={{
                  textAlign: "center",
                  borderRadius: 3,
                  boxShadow: 3,
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    You're currently a member of{" "}
                    <strong>{activeFBO?.name}</strong>
                    {fbos &&
                      fbos.length > 1 &&
                      ` and ${fbos.length - 1} other FBO${
                        fbos.length > 2 ? "s" : ""
                      }`}
                    .
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Looking for access to another FBO? Contact their
                    administrator for an invitation.
                  </Typography>

                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant="contained"
                      onClick={handleReturnToDashboard}
                      sx={{ fontWeight: "medium" }}
                    >
                      Return to Dashboard
                    </Button>
                    <Button variant="outlined" onClick={handleSwitchFBO}>
                      Manage FBOs
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              // User has no FBOs and no pending invites
              <Card
                sx={{
                  textAlign: "center",
                  borderRadius: 3,
                  boxShadow: 3,
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  {/* Main Message */}
                  <Typography variant="h5" fontWeight="medium" sx={{ mb: 2 }}>
                    No FBO Access
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    You're logged in as <strong>{airplxIdentity?.email}</strong>
                    , but don't have access to any FBOs yet.
                  </Typography>

                  {/* Action Steps */}
                  <Card variant="outlined" sx={{ textAlign: "left", mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="medium"
                        sx={{ mb: 2 }}
                      >
                        What to do next:
                      </Typography>
                      <Stack spacing={2}>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="flex-start"
                        >
                          <Typography color="primary" sx={{ mt: 0.5 }}>
                            📬
                          </Typography>
                          <Typography variant="body2">
                            Check your email for an invitation with the subject{" "}
                            <strong>"Claim AirPlx Invite"</strong>
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="flex-start"
                        >
                          <Typography color="primary" sx={{ mt: 0.5 }}>
                            👥
                          </Typography>
                          <Typography variant="body2">
                            Ask your FBO administrator to send you an invitation
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="flex-start"
                        >
                          <Typography color="primary" sx={{ mt: 0.5 }}>
                            💬
                          </Typography>
                          <Typography variant="body2">
                            Contact <strong>support@airplx.com</strong> if you
                            believe this is an error
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>

                  {/* Footer Note */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    Invitations are sent to the email address you used to log
                    in.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </Box>

      {/* Snackbar for user feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
